import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react';
import { get as getProjection } from 'ol/proj';
import "ol/ol.css";
import { RFeature, RFeatureProps, RFeatureUIEvent, RLayerImage, RLayerVector, RMap, RMapProps } from 'rlayers';
import { Point } from 'ol/geom';
import { useRecoilValue } from 'recoil';
import { configState } from '../../../recoil/atoms';
import { getSymbolCanvas } from './SymbolCreator';
import { uniqBy } from 'lodash';
import { RCircle, RFill, RIcon, RStroke, RStyle, useRStyle } from 'rlayers/style';
import { RStyleProps } from 'rlayers/style/RStyle';
import { MapInstance, MapItem } from './Map';
import { useImageWithAuthentication } from '../../../hooks/useImageWithAuthentication';
import Feature from 'ol/Feature';
import { getMercatorProjectionForSize, MERCATOR_SRS_ID } from '../../../hooks/MapUtils';
import { ViewOptions } from 'ol/View';
import { FloorPlanType } from '../../../contexts/AggregatesContext/types';

export type FloorPlanMapProps = {
  floorPlan: FloorPlanType;
  items?: MapItem[];
  maxZoom?: RMapProps['maxZoom'];
  onClick?: (props: { lat: number, lng: number }) => void,
  onClusterClick?: <F extends Feature>(ev: RFeatureUIEvent<F>) => void;
  onSymbolClick?:  <F extends Feature>(ev: RFeatureUIEvent<F>) => void;
  onMoveEnd?: RMapProps['onMoveEnd'];
  setMapInstance?: React.Dispatch<React.SetStateAction<MapInstance | null>>;
  selectedItemId?: string;
};

const DEFAULT_VIEW_OPTIONS = {
  center: [0, 0],
  constrainOnlyCenter: true,
  showFullExtent: true,
  smoothExtentConstraint: true,
  smoothResolutionConstraint: true,
  zoom: 1,
} satisfies ViewOptions;

const STYLE_KEY: keyof NonNullable<FloorPlanMapProps['items']>[number] = 'styleKey';

const FloorPlanMap: React.FC<PropsWithChildren<FloorPlanMapProps>> = ({
  children,
  floorPlan,
  items,
  maxZoom,
  onClick,
  onClusterClick,
  onSymbolClick,
  onMoveEnd,
  selectedItemId,
  setMapInstance,
}) => {
  const { imageData } = useImageWithAuthentication(floorPlan.image[0]);

  const projection = useMemo(() => {
    if (!imageData) {
      return getProjection(MERCATOR_SRS_ID)!;
    }

    const code = `TGO:${imageData.id}`;
    return getProjection(code) ?? getMercatorProjectionForSize([imageData.width, imageData.height], { code }, true);
  }, [imageData]);

  const extent = projection?.getExtent();
  const assetStyleRef = useRStyle();
  const selectedItemStyleRef = useRStyle();

  const mapRef = useRef<RMap>(null);
  const layerImageRef = useRef<RLayerImage>(null);
  const vectorLayerRef = useRef<RLayerVector>(null);

  useEffect(() => {
    if (!projection) {
      return;
    }

    const viewOptions: ViewOptions = {
      ...DEFAULT_VIEW_OPTIONS,
      extent: projection.getExtent(),
      projection,
    };
    const [w, s, e, n] = viewOptions.extent!;
    const newSize = [e-w, n-s];

    // RLayers does not currently support modified options when creating the OpenLayers `Map` and `View`. Some of them can be set manually.
    const olView = mapRef.current?.ol.getView();
    olView?.setViewportSize(newSize);
    olView?.setCenter(viewOptions.center);
    olView?.setZoom(viewOptions.zoom!);
    olView?.applyOptions_(olView.getUpdatedOptions_(viewOptions));  // TODO: [S5-3775] Any issues using these methods?

    mapRef.current?.ol.setSize(newSize);
    mapRef.current?.ol.updateSize();
    mapRef.current?.forceUpdate();

    layerImageRef.current?.ol.getLayerState().layer.setExtent(viewOptions.extent);
    layerImageRef.current?.ol.setExtent(viewOptions.extent);
    layerImageRef.current?.ol.changed();  
  }, [projection]);

  useEffect(() => {
    if (vectorLayerRef.current?.ol) {
      setTimeout(() => {
        vectorLayerRef.current!.ol.changed();
      }, 50);
    }
  }, [items]);

  const { symbolScaleFactor } = useRecoilValue(configState);

  const createSymbolCanvas = useCallback(
    (id: string) => {
      const [, , , , , prefSize, , font] = id?.split('_');
      const newScale = prefSize ? Number(prefSize) : symbolScaleFactor;
      const scaledSize = Math.round(82 * (newScale || 1) / 2); // TODO: [S5-3775] Is `/ 2` sufficient to emulate `{ pixelRatio: 2 }`? See `Map.tsx:StyleLoader`.
      return getSymbolCanvas(id, scaledSize, font);
    },
    [symbolScaleFactor]
  );

  const recordSymbolKeys = useMemo(() => uniqBy(items, a => a.styleKey).map(a => a.styleKey), [items]);

  const getCacheId = useCallback<NonNullable<RStyleProps['cacheId']>>((feature, _resolution) => feature.get(STYLE_KEY), []);
  const getRenderer = useCallback<NonNullable<RStyleProps['render']>>((feature, _resolution) => {
    const symbolKey: string | undefined = feature.get(STYLE_KEY);
    if (!symbolKey) {
      return <></>;
    }
    const canvas = createSymbolCanvas(symbolKey);
    return (
      <RIcon img={canvas} imgSize={[canvas.width, canvas.height]} />
    );
  }, [createSymbolCanvas]);

  const getSelectedItemCacheId = useCallback<NonNullable<RStyleProps['cacheId']>>((feature, _resolution) => `${feature.get(STYLE_KEY)}-selected`, []);
  const getSelectedItemRenderer = useCallback<NonNullable<RStyleProps['render']>>((feature, _resolution) => {
    const item = feature.getProperties() as MapItem<GeoJSON.Point>;
    // TODO: [S5-3775] Consolidate replicated code.
    const [, , , , , prefSize, , font] = item.styleKey?.split('_');
    const newScale = prefSize ? Number(prefSize) : symbolScaleFactor;
    const scaledSize = Math.round(20 * (newScale || 1) / 2); // TODO: [S5-3775] What base numeral should this be?
    // TODO: [S5-3775] Finalize selected style.
    return (
      <>
        <RCircle radius={scaledSize} scale={1.5}>
          <RFill color='white' />
          <RStroke color='black' width={1.5} />
        </RCircle>
      </>
    );
  }, []);

  const onMapClick = useCallback<NonNullable<RMapProps['onClick']>>((evt) => {
    const [lng, lat] = evt.coordinate;
    onClick?.({ lat, lng });
  }, [onClick]);

  const onFeaturePointClick = useCallback<NonNullable<RFeatureProps<Point>['onClick']>>((ev) => {
    const id: string | undefined = ev.target.get('id');
    if (id?.match(/Cluster-/)) { // TODO: [S5-3775] Do we need cluster checking here? Or let symbol wrapper handle?
      onClusterClick?.(ev);
    } else {
      onSymbolClick?.(ev);
    }
  }, [onClusterClick, onSymbolClick]);

  const mapImage = useMemo(() => (
    (imageData && extent)
      ? <RLayerImage ref={layerImageRef} url={imageData.src} extent={extent} projection={projection} wrapX={false} size={[extent[2]-extent[0], extent[3]-extent[1]]} />
      : <></>
  ), [imageData, extent, projection]);

  useEffect(() => {
    setMapInstance?.({
      fitBounds: (_bounds) => {
        // This is used for `LineStrings`, which are not yet supported for floor plans.
      },
      getZoom: () => {
        const olView = mapRef.current?.ol.getView();
        return olView?.getZoom.bind(olView)() ?? DEFAULT_VIEW_OPTIONS.zoom;
      },
      moveTo: ({ center }) => {
        const olView = mapRef.current?.ol.getView();
        olView?.animate.bind(olView)({ center });
      }
    });
  }, [setMapInstance, mapRef.current]);

  // TODO: [S5-3775] Do we need to remove features when changing maps, so they don't temporarily overlay the new floor plan?
  const vectorPoints = useMemo(() => (
    items?.filter(item => item.location.type === 'Point').map(item => (
      <RFeature<Point>
        key={item.id}
        geometry={new Point((item.location as GeoJSON.Point).coordinates)}
        onClick={onFeaturePointClick}
        properties={{ assetId: item.id, ...item }}
      />
    ))
  ), [
    items,
    recordSymbolKeys,
  ]);

  const selectedItem = useMemo(() => !!selectedItemId ? items?.find(x => x.id === selectedItemId) : undefined, [items, selectedItemId]);

  return (!imageData || !extent) ? <>Loading...</> : ( // TODO: [S5-3775] Loading placeholder.
    <>
      <RStyle
        ref={assetStyleRef}
        cacheSize={1024}  // TODO: [S5-3775] Is this needed?
        cacheId={getCacheId}
        render={getRenderer}
      />
      <RStyle
        ref={selectedItemStyleRef}
        cacheId={getSelectedItemCacheId}
        render={getSelectedItemRenderer}
      />
      <RMap
        ref={mapRef}
        width={"100%"}
        height={"100%"}
        initial={DEFAULT_VIEW_OPTIONS}
        projection={projection}
        onMoveEnd={onMoveEnd}
        onClick={onMapClick}
        maxZoom={maxZoom}
        noDefaultControls={true}
      >
        {mapImage}
        <RLayerVector
          ref={vectorLayerRef}
          onAddFeature={e => {
            e.feature?.setStyle((f, r) => assetStyleRef.current?.style(f as Feature, r));  // TODO: [S5-3775] Why does this work, but `style` props don't?
          }}
          style={assetStyleRef}
          renderBuffer={99999999} // TODO: [S5-3775] What's reasonable for this? From projection/extent?
          wrapX={false}
          zIndex={2}
          >
          {vectorPoints}
        </RLayerVector>
        <RLayerVector
          onAddFeature={e => {
            e.feature?.setStyle((f, r) => selectedItemStyleRef.current?.style(f as Feature, r));  // TODO: [S5-3775] Why does this work, but `style` props don't?
          }}
          style={selectedItemStyleRef}
          renderBuffer={99999999} // TODO: [S5-3775] What's reasonable for this? From projection/extent?
          wrapX={false}
          zIndex={1}
        >
          {selectedItem && (
            <RFeature<Point>
              key={`${selectedItemId}-selected`}
              geometry={new Point((selectedItem.location as GeoJSON.Point).coordinates)}
              properties={{ assetId: selectedItem.id, ...selectedItem }}
            />
          )}
        </RLayerVector>
        {children}
      </RMap>
    </>
  );
};

export default FloorPlanMap;
